import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { Bell, X } from 'lucide-react';
import { isAuthenticated, getLicenseStatus } from '../services/auth';

const Pricing = () => {
  const { darkMode } = useContext(ThemeContext);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [hasNotified, setHasNotified] = useState(false);
  const [hideTrialBanner, setHideTrialBanner] = useState(isAuthenticated()); // Hide by default for authenticated users
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  useEffect(() => {
    const checkLicenseStatus = async () => {
      if (isAuthenticated()) {
        try {
          const status = await getLicenseStatus();
          // Hide trial prompts if user has/had a trial or has a paid license
          setHideTrialBanner(status.trialLicense || status.hasPaidLicense);
          // Check if user has a paid license (active, trialing, or cancelled but not expired)
          setHasActiveSubscription(status.hasPaidLicense);
        } catch (error) {
          // If we can't check license status, show the trial prompts
          console.error('Error checking license status:', error);
          setHideTrialBanner(false);
        }
      } else {
        setHideTrialBanner(false);
      }
    };
    checkLicenseStatus();
  }, []);


  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-gray-100 dark:bg-gray-900`}>
      <div className="px-4 sm:px-6 lg:px-8 flex-grow">
        <div className="max-w-[1440px] mx-auto py-10">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Pricing</h1>
        
        {/* Free Trial Banner - Hidden for users who have/had a trial */}
        {!hideTrialBanner && (
          <div className="mb-12 bg-green-200 dark:bg-green-900/30 rounded-lg border border-green-400 dark:border-green-800 p-4 sm:p-6" id="free-trial">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center">
                <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-green-600 dark:bg-green-700">
                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-green-950 dark:text-green-300">Try Stonewall Invoicing Free for 7 Days</p>
                  <p className="text-sm text-green-900 dark:text-green-400">
                    Get full access to all premium features — no credit card required. No commitment.
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0 sm:ml-4">
                <Link
                  to="/register"
                  className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-green-700 hover:bg-green-800 dark:bg-green-700 dark:hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  Start Free Trial
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Referral Program Banner - Commented out - keeping code for future use */}
        {/* <div className="mb-16">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 dark:from-purple-700 dark:to-pink-700 rounded-xl shadow-xl overflow-hidden">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-3/4 p-8 md:p-10">
                <div className="flex items-center mb-3">
                  <Share2 className="w-6 h-6 text-purple-100 mr-2" />
                  <h3 className="text-2xl font-bold text-white">Referral Program</h3>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">
                  Refer 10 Friends, Get 2 Years Access FREE!
                </h4>
                <p className="text-purple-100 mb-4">
                  Share Stonewall Invoicing with friends and colleagues to earn rewards. For every 10 paying subscribers you refer, you'll receive 2 full years of Stonewall Invoicing access - completely free! Plus, your referrals get a free month when they sign up.
                </p>
                <Link
                  to="/account/referrals"
                  className="mt-2 bg-white text-purple-600 hover:bg-purple-50 px-6 py-2 rounded-md text-sm font-medium inline-flex items-center"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Get Referral Link
                </Link>
              </div>
              <div className="md:w-1/4 p-6 flex items-center justify-center">
                <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full">
                  <Gift className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Professional Plan - Landscape */}
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-xl overflow-hidden border-2 border-blue-500 mb-8">
          <div className="bg-blue-500 py-2 text-center">
            <span className="text-white font-medium">STONEWALL INVOICING</span>
          </div>
          <div className="grid md:grid-cols-2 gap-6 p-8">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Stonewall Invoicing</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Our flagship product provides a complete solution for your automotive service business. With one simple subscription you get access to all our professional features.
              </p>

              {/* Subscription Options */}
              <div className="mb-6">
                <div
                  className="bg-gray-200 dark:bg-gray-600 rounded-lg p-3 flex justify-between mb-2 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                >
                  <span className="font-medium dark:text-gray-200">Monthly</span>
                  <span className="font-bold dark:text-white">$119.99/mo</span>
                </div>
                <div
                  className="bg-gray-200 dark:bg-gray-600 rounded-lg p-3 flex justify-between mb-2 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                >
                  <span className="font-medium dark:text-gray-200">3 Months</span>
                  <span className="font-bold dark:text-white">$109.99/mo <span className="text-green-600 dark:text-green-400">Save $30</span></span>
                </div>
                <div
                  className="bg-gray-200 dark:bg-gray-600 rounded-lg p-3 flex justify-between mb-2 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                >
                  <span className="font-medium dark:text-gray-200">6 Months</span>
                  <span className="font-bold dark:text-white">$99.99/mo <span className="text-green-600 dark:text-green-400">Save $120</span></span>
                </div>
                <div
                  className="bg-blue-200 dark:bg-blue-800 rounded-lg p-3 flex justify-between cursor-pointer hover:bg-blue-300 dark:hover:bg-blue-700 transition-colors"
                  onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                >
                  <span className="font-medium dark:text-gray-200">Annual</span>
                  <span className="font-bold dark:text-white">$999/year <span className="text-green-600 dark:text-green-400">Save $440</span></span>
                </div>
              </div>

              <div className="flex items-baseline">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">Save up to 31%</span>
              </div>
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                with annual subscription
              </div>
            </div>

            <div className="flex flex-col">
              <ul className="space-y-3 flex-grow">
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Single User License</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Unlimited Invoices</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Advanced Customer Management</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300"><strong>Complete Vehicle Database</strong> with <strong>FREE yearly updates</strong></span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Comprehensive Reports and Analytics</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Email Notifications and Reminders</span>
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">Easy PDF Invoice Generation</span>
                </li>
              </ul>
              <Link
                to={isAuthenticated() ? "/account/subscription" : "/register"}
                className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md text-sm font-medium inline-block text-center transition-colors"
              >
                {hasActiveSubscription ? 'Manage Subscription' : 'Subscribe Now'}
              </Link>
            </div>
          </div>
        </div>

        {/* Cloud Version - Coming Soon */}
        <div className="bg-indigo-600 dark:bg-indigo-700 rounded-lg shadow-lg overflow-hidden">
            <div className="bg-blue-600 dark:bg-blue-800 py-1.5 text-center">
              <span className="text-white text-sm font-medium">COMING SOON</span>
            </div>
            <div className="p-5">
              <h3 className="text-xl font-bold text-white mb-2">Stonewall Cloud</h3>
              <p className="text-blue-100 text-sm mb-4">
                Access your business data anytime, anywhere with our cloud-connected version. Collaborate with your team in real-time and enjoy automatic backups and updates.
              </p>
              
              {/* Pricing Coming Soon */}
              <div className="bg-blue-500/20 backdrop-blur-sm rounded-lg p-3 mb-4">
                <div className="text-center">
                  <p className="text-base font-bold text-white">Pricing will be announced soon</p>
                </div>
              </div>

              <div className="space-y-2 mb-4 text-white text-sm">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-blue-200 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>All Professional features included</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-blue-200 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Unlimited Users</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-blue-200 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Cloud invoice storage and data sync</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-blue-200 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Real-time synchronization</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-blue-200 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Cross-device compatibility</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-blue-200 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Automatic cloud backups</span>
                </div>
              </div>

              <button
                className={`w-full py-2 rounded-md text-sm font-medium inline-flex items-center justify-center ${
                  hasNotified
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-white text-indigo-600 hover:bg-indigo-50'
                }`}
                onClick={() => setShowNotifyModal(true)}
              >
                {hasNotified ? (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Thank You!
                  </>
                ) : (
                  <>
                    <Bell className="h-4 w-4 mr-2" />
                    Notify Me
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>
      
      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative transform transition-all">
            <button
              onClick={() => setShowNotifyModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Get Notified About Stonewall Cloud</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Be the first to know when our cloud solution launches. Enter your email below and we'll keep you updated!
              </p>
            </div>
            
            {!hasNotified ? (
              <form onSubmit={(e) => {
                e.preventDefault();
                setHasNotified(true);
              }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </button>
              </div>
            </form>
            ) : (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Thank You!</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    We've added {notifyEmail} to our notification list. You'll be the first to know when Stonewall Cloud launches!
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowNotifyModal(false);
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;